class DashboardProfileCompleteness extends HTMLElement {
  connectedCallback() {
    this._totalFields = Number(this.dataset.totalFields || 4);
    this._profile = {
      first_name: this.dataset.firstName || '',
      last_name: this.dataset.lastName || '',
      email: this.dataset.email || '',
      phone: this.dataset.phone || '',
    };
    this._lensProfile = { power_right: null, power_left: null };
    this._i18n = this._readI18n();

    this._fetchLensProfile();
    this._bindEditTrigger();
  }

  _readI18n() {
    return {
      dialogHeading: this.dataset.i18nDialogHeading || '',
      fieldsFilledTemplate: this.dataset.i18nFieldsFilledTemplate || '',
      firstNameLabel: this.dataset.i18nFirstNameLabel || '',
      lastNameLabel: this.dataset.i18nLastNameLabel || '',
      whatsappLabel: this.dataset.i18nWhatsappLabel || '',
      saveLabel: this.dataset.i18nSaveLabel || '',
      cancelLabel: this.dataset.i18nCancelLabel || '',
      successMessage: this.dataset.i18nSuccessMessage || '',
      genericError: this.dataset.i18nGenericError || '',
      requiredError: this.dataset.i18nRequiredError || '',
    };
  }

  _animateBar(percent) {
    const barEl = this.querySelector('[data-stat="completeness-bar"]');
    if (!barEl) return;
    // Force a reflow before setting the width so the transition animates from
    // the initial 0% instead of snapping instantly (same technique as the
    // welcome-hero tier-progress-bar).
    void barEl.offsetWidth;
    barEl.style.width = `${Math.max(0, Math.min(100, percent))}%`;
  }

  _fetchLensProfile() {
    const endpoint = this.dataset.endpoint || '/apps/lensza/reorder-summary';

    fetch(endpoint)
      .then((r) => {
        if (!r.ok) throw new Error(`reorder-summary failed: ${r.status}`);
        return r.json();
      })
      .then((data) => this._render(data))
      .catch(() => {
        // Fetch failed — fall back to the server-rendered known fields only.
        this._renderLensProfile(null);
        this._recomputeCompleteness();
      })
      .finally(() => {
        this._revealEditTrigger();
        this._stopLoading();
      });
  }

  _stopLoading() {
    this.removeAttribute('data-loading');
    this.removeAttribute('aria-busy');
  }

  _revealEditTrigger() {
    // The Edit button stays hidden until the lens-profile fetch settles, so it
    // never opens a dialog pre-filled with a still-unknown prescription value.
    const trigger = document.querySelector('[data-profile-edit-trigger]');
    if (trigger) trigger.classList.remove('hidden');
  }

  _render(data) {
    const hasPower = Boolean(
      data && data.has_profile && (data.power_right || data.power_left),
    );
    this._lensProfile = {
      power_right: hasPower ? data.power_right : null,
      power_left: hasPower ? data.power_left : null,
    };
    this._renderLensProfile(hasPower ? data : null);
    this._recomputeCompleteness();
  }

  _renderLensProfile(data) {
    const el = this.querySelector('[data-stat="lens-profile"]');
    if (!el) return;

    if (!data) {
      el.textContent = '—';
      el.classList.add('text-text-muted');
      return;
    }

    const parts = [];
    if (data.power_right) parts.push(`R ${data.power_right}`);
    if (data.power_left) parts.push(`L ${data.power_left}`);
    el.textContent = parts.join(' / ');
    el.classList.remove('text-text-muted');
  }

  _recomputeCompleteness() {
    let filledCount = 0;
    if (this._profile.first_name.trim() || this._profile.last_name.trim())
      filledCount += 1;
    if (this._profile.email.trim()) filledCount += 1;
    if (this._profile.phone.trim()) filledCount += 1;
    if (this._lensProfile.power_right || this._lensProfile.power_left)
      filledCount += 1;

    const percent = Math.round((filledCount / this._totalFields) * 100);

    const labelEl = this.querySelector('[data-stat="fields-filled-label"]');
    if (labelEl) {
      labelEl.textContent = this._i18n.fieldsFilledTemplate
        .replace('%filled%', filledCount)
        .replace('%total%', this._totalFields);
    }

    const percentEl = this.querySelector('[data-stat="fields-filled-percent"]');
    if (percentEl) percentEl.textContent = `${percent}%`;

    this._animateBar(percent);
  }

  _escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML;
  }

  _bindEditTrigger() {
    const trigger = document.querySelector('[data-profile-edit-trigger]');
    if (trigger)
      trigger.addEventListener('click', () => this._openEditDialog());
  }

  _openEditDialog() {
    const dialogModal = document.getElementById('DialogModal');
    if (
      !dialogModal ||
      typeof dialogModal.setContent !== 'function' ||
      typeof dialogModal.openModal !== 'function'
    ) {
      return;
    }

    const i18n = this._i18n;
    const profile = this._profile;
    const lens = this._lensProfile;

    if (typeof dialogModal.setSize === 'function') dialogModal.setSize('small');

    dialogModal.setContent(`
      <div class="px-5 lg:px-8 py-6 md:py-8" data-edit-profile-dialog>
        <h3 class="font-heading font-bold text-xl mb-4">${this._escapeHtml(i18n.dialogHeading)}</h3>

        <div class="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label class="block body-sm font-medium form-field__label mb-1" for="edit-profile-first-name">${this._escapeHtml(i18n.firstNameLabel)}</label>
            <input id="edit-profile-first-name" type="text" value="${this._escapeHtml(profile.first_name)}" class="w-full rounded-lg border transition-colors duration-200 form-field__input h-10 px-3 body-sm" data-field="first_name" />
          </div>
          <div>
            <label class="block body-sm font-medium form-field__label mb-1" for="edit-profile-last-name">${this._escapeHtml(i18n.lastNameLabel)}</label>
            <input id="edit-profile-last-name" type="text" value="${this._escapeHtml(profile.last_name)}" class="w-full rounded-lg border transition-colors duration-200 form-field__input h-10 px-3 body-sm" data-field="last_name" />
          </div>
        </div>

        <div class="mb-3">
          <label class="block body-sm font-medium form-field__label mb-1" for="edit-profile-phone">${this._escapeHtml(i18n.whatsappLabel)}</label>
          <input id="edit-profile-phone" type="tel" value="${this._escapeHtml(profile.phone)}" class="w-full rounded-lg border transition-colors duration-200 form-field__input h-10 px-3 body-sm" data-field="phone" />
        </div>

        <p class="body-xs text-error mb-3 hidden" data-edit-profile-error></p>

        <div class="grid grid-cols-2 gap-3 mb-4">
          <div data-power-right-slot></div>
          <div data-power-left-slot></div>
        </div>

        <div class="flex gap-2 mt-4">
          <button type="button" class="btn relative rounded-full btn--secondary-outline btn--md flex-1" data-dialog-close>
            <span class="btn-content w-max"><span class="btn-text">${this._escapeHtml(i18n.cancelLabel)}</span></span>
          </button>
          <button type="button" class="btn relative rounded-full btn--accent btn--md flex-1" data-edit-profile-submit>
            <span class="btn-content w-max"><span class="btn-text">${this._escapeHtml(i18n.saveLabel)}</span></span>
            <span class="btn-spinner absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden">
              <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
          </button>
        </div>
      </div>
    `);
    dialogModal.openModal();

    const root = dialogModal.querySelector('[data-edit-profile-dialog]');
    const powerRightSelectEl = this._mountPowerSelect(root, '[data-power-right-slot]', '[data-power-right-template]', lens.power_right);
    const powerLeftSelectEl = this._mountPowerSelect(root, '[data-power-left-slot]', '[data-power-left-template]', lens.power_left);
    this._bindEditDialogEvents(root, dialogModal, powerRightSelectEl, powerLeftSelectEl);
  }

  _mountPowerSelect(root, slotSelector, templateSelector, value) {
    const slot = root && root.querySelector(slotSelector);
    const template = this.querySelector(templateSelector);
    if (!slot || !template || !template.content) return null;

    const inputSelectEl = template.content.firstElementChild.cloneNode(true);
    // Must be connected to the document before <input-select>'s connectedCallback
    // wires up this.select/this.trigger/this.dropdown and setValue() becomes usable.
    slot.replaceWith(inputSelectEl);

    if (value && typeof inputSelectEl.setValue === 'function') {
      inputSelectEl.setValue(value);
    }

    // Return the underlying native <select> directly so the submit handler reads
    // from a stable element reference instead of re-querying by id later — the
    // dropdown UL gets teleported to <body> on connect, so id-based lookups
    // scoped to the dialog root are not something we want to depend on.
    return inputSelectEl.querySelector('select');
  }

  _bindEditDialogEvents(root, dialogModal, powerRightSelectEl, powerLeftSelectEl) {
    if (!root) return;
    const submitBtn = root.querySelector('[data-edit-profile-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', () =>
        this._submitProfile(root, dialogModal, submitBtn, powerRightSelectEl, powerLeftSelectEl),
      );
    }
  }

  _setDialogError(root, message) {
    const el = root.querySelector('[data-edit-profile-error]');
    if (!el) return;
    el.textContent = message || '';
    el.classList.toggle('hidden', !message);
  }

  async _submitProfile(root, dialogModal, submitBtn, powerRightSelectEl, powerLeftSelectEl) {
    const i18n = this._i18n;
    const getValue = (field) =>
      (root.querySelector(`[data-field="${field}"]`)?.value || '').trim();

    const firstName = getValue('first_name');
    const lastName = getValue('last_name');
    const phone = getValue('phone');
    const powerRight = (powerRightSelectEl?.value || '').trim();
    const powerLeft = (powerLeftSelectEl?.value || '').trim();

    if (!firstName || !lastName || !phone) {
      this._setDialogError(root, i18n.requiredError);
      return;
    }

    this._setDialogError(root, '');
    submitBtn.classList.add('loading');
    submitBtn.disabled = true;

    try {
      const res = await fetch(
        this.dataset.updateEndpoint || '/apps/lensza/update-profile',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: firstName,
            last_name: lastName,
            phone,
            power_right: powerRight || null,
            power_left: powerLeft || null,
          }),
        },
      );
      const body = await res.json().catch(() => ({}));

      if (!res.ok || body.status !== 'ok') {
        const fieldError =
          body.field_errors && Object.values(body.field_errors)[0];
        this._setDialogError(
          root,
          fieldError || body.detail || i18n.genericError,
        );
        submitBtn.classList.remove('loading');
        submitBtn.disabled = false;
        return;
      }

      this._profile = {
        ...this._profile,
        first_name: firstName,
        last_name: lastName,
        phone,
      };
      this._lensProfile = {
        power_right: powerRight || null,
        power_left: powerLeft || null,
      };

      this._applyProfileToCard();
      this._recomputeCompleteness();

      // Profile changed — let other cards refetch if they also display these fields.
      document.dispatchEvent(
        new CustomEvent('lensza:profile-updated', { detail: body }),
      );

      root.innerHTML = `<p class="body-base text-center py-4">${this._escapeHtml(i18n.successMessage)}</p>`;
      setTimeout(() => dialogModal.close(), 1100);
    } catch (e) {
      this._setDialogError(root, i18n.genericError);
      submitBtn.classList.remove('loading');
      submitBtn.disabled = false;
    }
  }

  _applyProfileToCard() {
    const fullName =
      `${this._profile.first_name} ${this._profile.last_name}`.trim();
    const nameEl = this.querySelector('[data-stat="full-name"]');
    if (nameEl) nameEl.textContent = fullName || '—';

    const emailEl = this.querySelector('[data-stat="email"]');
    if (emailEl) emailEl.textContent = this._profile.email || '—';

    const phoneEl = this.querySelector('[data-stat="phone"]');
    if (phoneEl) phoneEl.textContent = this._profile.phone || '—';

    this._renderLensProfile(
      this._lensProfile.power_right || this._lensProfile.power_left
        ? this._lensProfile
        : null,
    );
  }
}

if (!customElements.get('dashboard-profile-completeness')) {
  customElements.define(
    'dashboard-profile-completeness',
    DashboardProfileCompleteness,
  );
}
